import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { trackScrollDepth, trackTimeOnPage, trackEngagementScore } from '@/lib/analytics';
import {
  calculateScrollScore,
  calculateTimeScore,
  calculateEngagementScore,
  getEngagementCategory,
  getPageType,
} from '@/lib/engagementScore';

const SCROLL_MILESTONES = [25, 50, 75, 90, 100] as const;
type ScrollMilestone = (typeof SCROLL_MILESTONES)[number];

// Idle detection threshold (30 seconds)
const IDLE_THRESHOLD_MS = 30000;
const ACTIVITY_THROTTLE_MS = 1000;

// Activity events to monitor for idle detection
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
] as const;

// Throttle utility
function throttle<T extends (...args: unknown[]) => void>(fn: T, delay: number): T {
  let lastCall = 0;
  return ((...args: unknown[]) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn(...args);
    }
  }) as T;
}

/**
 * Combined engagement tracking hook that monitors scroll depth, time on page,
 * and content interactions to calculate a weighted engagement score.
 * 
 * Consolidates useScrollDepthTracking and useTimeOnPageTracking into a single hook.
 * Sends individual milestone events for backward compatibility plus combined score on exit.
 */
export function useEngagementTracking() {
  const location = useLocation();

  // Scroll tracking
  const maxScrollDepth = useRef<number>(0);
  const trackedMilestones = useRef<Set<ScrollMilestone>>(new Set());

  // Time tracking
  const startTime = useRef<number>(Date.now());
  const accumulatedTime = useRef<number>(0);
  const isVisible = useRef<boolean>(true);
  const currentPath = useRef<string>(location.pathname);

  // Interaction tracking
  const interactionCount = useRef<number>(0);

  // Idle detection
  const isIdle = useRef<boolean>(false);
  const idleTimeoutId = useRef<number | null>(null);

  /**
   * Track a content interaction (click, sort, filter, expand, etc.)
   * Call this from components to boost engagement score
   */
  const trackInteraction = useCallback(() => {
    interactionCount.current++;
  }, []);

  useEffect(() => {
    // Reset all metrics on route change
    maxScrollDepth.current = 0;
    trackedMilestones.current.clear();
    startTime.current = Date.now();
    accumulatedTime.current = 0;
    isVisible.current = true;
    currentPath.current = location.pathname;
    interactionCount.current = 0;
    isIdle.current = false;
    if (idleTimeoutId.current) {
      clearTimeout(idleTimeoutId.current);
      idleTimeoutId.current = null;
    }

    const calculateTotalSeconds = (): number => {
      let total = accumulatedTime.current;
      // Only add current session time if visible AND not idle
      if (isVisible.current && !isIdle.current) {
        total += Date.now() - startTime.current;
      }
      return Math.round(total / 1000);
    };

    const calculateScrollPercent = (): number => {
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      const scrollTop = window.scrollY;
      const scrollableHeight = scrollHeight - clientHeight;
      
      if (scrollableHeight <= 0) return 100; // Non-scrollable page
      return Math.round((scrollTop / scrollableHeight) * 100);
    };

    const sendEngagementScore = (exitMethod: 'navigation' | 'tab_close') => {
      const totalSeconds = calculateTotalSeconds();
      const pageType = getPageType(currentPath.current);
      
      const scrollScore = calculateScrollScore(maxScrollDepth.current);
      const timeScore = calculateTimeScore(totalSeconds, pageType);
      const score = calculateEngagementScore(
        scrollScore,
        timeScore,
        interactionCount.current
      );
      const category = getEngagementCategory(score);

      // Send combined engagement score event
      trackEngagementScore(score, category, {
        scroll_score: scrollScore,
        time_score: timeScore,
        interaction_count: interactionCount.current,
        time_seconds: totalSeconds,
        max_scroll_depth: maxScrollDepth.current,
      }, currentPath.current);

      // Also send individual time event for backward compatibility
      if (totalSeconds > 0) {
        trackTimeOnPage(totalSeconds, document.title, currentPath.current, exitMethod);
      }
    };

    // Reset idle timer on user activity
    const resetIdleTimer = () => {
      // If coming back from idle, resume timing
      if (isIdle.current && isVisible.current) {
        startTime.current = Date.now();
        isIdle.current = false;
      }

      // Clear existing timeout
      if (idleTimeoutId.current) {
        clearTimeout(idleTimeoutId.current);
      }

      // Set new idle timeout
      idleTimeoutId.current = window.setTimeout(() => {
        if (isVisible.current && !isIdle.current) {
          // Accumulate active time before going idle
          accumulatedTime.current += Date.now() - startTime.current;
          isIdle.current = true;
        }
      }, IDLE_THRESHOLD_MS);
    };

    // Throttled activity handler to avoid performance issues
    const handleActivity = throttle(() => {
      resetIdleTimer();
    }, ACTIVITY_THROTTLE_MS);

    // Scroll handler
    const handleScroll = throttle(() => {
      const scrollPercent = calculateScrollPercent();
      maxScrollDepth.current = Math.max(maxScrollDepth.current, scrollPercent);

      // Check and send individual milestone events
      for (const milestone of SCROLL_MILESTONES) {
        if (scrollPercent >= milestone && !trackedMilestones.current.has(milestone)) {
          trackedMilestones.current.add(milestone);
          trackScrollDepth(milestone, document.title, location.pathname);
        }
      }
    }, 200);

    // Visibility handler (pause/resume time)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Accumulate time only if we weren't already idle
        if (!isIdle.current) {
          accumulatedTime.current += Date.now() - startTime.current;
        }
        isVisible.current = false;
        
        // Clear idle timer when hidden
        if (idleTimeoutId.current) {
          clearTimeout(idleTimeoutId.current);
          idleTimeoutId.current = null;
        }
      } else {
        startTime.current = Date.now();
        isVisible.current = true;
        isIdle.current = false;
        
        // Restart idle timer
        resetIdleTimer();
      }
    };

    // Page unload handler
    const handleBeforeUnload = () => {
      sendEngagementScore('tab_close');
    };

    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Add activity listeners for idle detection
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, handleActivity, { passive: true });
    }

    // Initial scroll check and start idle timer
    handleScroll();
    resetIdleTimer();

    // Cleanup and send score on route change
    return () => {
      sendEngagementScore('navigation');
      
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Remove activity listeners
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, handleActivity);
      }
      
      // Clear idle timeout
      if (idleTimeoutId.current) {
        clearTimeout(idleTimeoutId.current);
      }
    };
  }, [location.pathname]);

  return { trackInteraction };
}
